import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useApi, useApp, hasRole } from "../context/AppContext";
import DeviceChannel from "../lib/deviceChannel";
import DeviceTerminal from "../components/DeviceTerminal";

/**
 * Remote console for one device.
 *
 * Standard mode is what this page is for: live logs and a palette of safe
 * commands, no shell. The command palette is rendered from the list the device
 * itself advertises in `hello`, not from a hardcoded copy — a device running an
 * older agent should offer what it actually supports.
 *
 * Full access is requested here but never assumed: the page shows what the
 * server says is in force, and the device makes its own decision regardless.
 */

const STATUS_LABEL = {
  idle: "not connected",
  connecting: "connecting…",
  open: "connected",
  ready: "connected",
  closed: "connection closed",
  reconnecting: "reconnecting…",
  error: "connection error",
  unavailable: "channel unavailable (mock mode)",
};

function Badge({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-900",
    red: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function formatRemaining(seconds) {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m} min ${s} s` : `${s} s`;
}

export default function DeviceConsolePage(props) {
  // From the route (/devices/:id/console) or from the cabinet page's Console tab.
  const { deviceId: routeDeviceId } = useParams();
  const deviceId = props?.deviceId || routeDeviceId;
  const api = useApi();
  const { user } = useApp();
  const isAdmin = hasRole(user, "tenant_admin");

  const [device, setDevice] = useState(null);
  const [access, setAccess] = useState(null);
  const [status, setStatus] = useState("idle");
  const [hello, setHello] = useState(null);
  const [lines, setLines] = useState([]);
  const [dropped, setDropped] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("");
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [ptySession, setPtySession] = useState(null);
  const [hasPty, setHasPty] = useState(false);

  const channelRef = useRef(null);
  const logEndRef = useRef(null);
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  // -- data -------------------------------------------------------------

  const refreshAccess = useCallback(async () => {
    try {
      setAccess(await api.getDeviceAccess(deviceId));
    } catch (err) {
      setError(err.message || String(err));
    }
  }, [api, deviceId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api.getDevice(deviceId);
        if (alive) setDevice(d);
      } catch (err) {
        if (alive) setError(err.message || String(err));
      }
    })();
    refreshAccess();
    return () => {
      alive = false;
    };
  }, [api, deviceId, refreshAccess]);

  // A grant expires on its own, so poll while one is live to keep the countdown
  // and the badge honest without the operator reloading.
  useEffect(() => {
    if (!access || access.mode !== "full") return undefined;
    const t = setInterval(refreshAccess, 15000);
    return () => clearInterval(t);
  }, [access, refreshAccess]);

  // -- channel ----------------------------------------------------------

  const onEnvelope = useCallback((env) => {
    if (env.ch === "sys" && env.t === "hello") {
      setHello(env.d);
      return;
    }
    if (env.ch === "sys" && env.t === "mode") {
      setAccess(env.d);
      return;
    }
    if (env.ch === "cmd" && env.t === "res") {
      const chan = channelRef.current;
      if (chan && chan.resolveCommand(env)) return;
      setOutput(env.d);
      return;
    }
    if (env.ch === "log" && env.t === "batch") {
      const batch = env.d || {};
      if (batch.dropped) setDropped((n) => n + batch.dropped);
      if (pausedRef.current || !batch.lines || !batch.lines.length) return;
      // Bounded buffer: a device under load would otherwise grow this without limit.
      setLines((prev) => [...prev, ...batch.lines].slice(-3000));
      return;
    }
    if (env.ch === "pty") {
      const chan = channelRef.current;
      if (chan) chan.dispatchPty(env);
      if (env.t === "denied") setError(`the device refused the shell request: ${env.d?.reason}`);
      if (env.t === "closed") setPtySession(null);
    }
  }, []);

  const wantPty = isAdmin && access?.mode === "full";

  useEffect(() => {
    const channel = new DeviceChannel({
      // Ask for the shell only when the grant is in force. A refusal is a
      // normal answer, not a failure: fall back to the read-only capabilities
      // so the console still works.
      fetchToken: async () => {
        if (wantPty) {
          try {
            const tok = await api.createConsoleToken(deviceId, ["logs", "cmd", "pty"]);
            setHasPty((tok.capabilities || []).includes("pty"));
            return tok;
          } catch {
            setHasPty(false);
          }
        } else {
          setHasPty(false);
        }
        return api.createConsoleToken(deviceId, ["logs", "cmd"]);
      },
      onEnvelope,
      onStatus: (s) => setStatus(s),
    });
    channelRef.current = channel;
    channel.connect();
    return () => {
      channel.close();
      channelRef.current = null;
    };
    // Reconnecting when the grant changes is what upgrades a read-only console
    // into a shell without a page reload.
  }, [api, deviceId, onEnvelope, wantPty]);

  useEffect(() => {
    if (!paused && logEndRef.current) logEndRef.current.scrollIntoView({ block: "end" });
  }, [lines, paused]);

  // -- actions ----------------------------------------------------------

  const toggleStream = () => {
    const chan = channelRef.current;
    if (!chan) return;
    if (streaming) {
      chan.unsubscribeLogs();
      setStreaming(false);
    } else {
      chan.subscribeLogs(200);
      setStreaming(true);
    }
  };

  const runCommand = async (command, args = {}) => {
    const chan = channelRef.current;
    if (!chan) return;
    setBusy(command);
    setError(null);
    setOutput(null);
    try {
      setOutput(await chan.runCommand(command, args));
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  };

  const grantFull = async () => {
    const reason = window.prompt("Reason for requesting full access (goes to the audit log):");
    if (!reason || !reason.trim()) return;
    const ttl = Number(window.prompt("For how many minutes? (at most 240)", "30")) || 30;
    setError(null);
    try {
      setAccess(await api.grantDeviceFullAccess(deviceId, { ttlMinutes: ttl, reason: reason.trim() }));
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  const revokeFull = async () => {
    try {
      setAccess(await api.revokeDeviceFullAccess(deviceId));
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  // -- render -----------------------------------------------------------

  const commands = hello?.commands || [];
  const visibleLines = useMemo(() => {
    if (!filter.trim()) return lines;
    const needle = filter.toLowerCase();
    return lines.filter((l) => l.toLowerCase().includes(needle));
  }, [lines, filter]);

  const mode = access?.mode || "standard";
  const remaining = formatRemaining(access?.expires_in_seconds);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {!props?.deviceId && (
            <Link to={`/cabinets/${deviceId}`} className="text-sm text-slate-500 hover:underline">
              ← the cabinet
            </Link>
          )}
          <h1 className="text-xl font-semibold">
            Console: {device?.name || deviceId}
          </h1>
          <p className="text-xs text-slate-500">{deviceId}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={status === "open" || status === "ready" ? "green" : status === "error" ? "red" : "slate"}>
            {STATUS_LABEL[status] || status}
          </Badge>
          <Badge tone={mode === "full" ? "amber" : "slate"}>
            {mode === "full" ? `full access${remaining ? ` · ${remaining}` : ""}` : "normal mode"}
          </Badge>
        </div>
      </div>

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
      )}

      {isAdmin && (
        <div className="rounded border border-slate-200 bg-white p-3 text-sm">
          {mode === "full" ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Full access granted
                {access?.granted_by_email ? ` (${access.granted_by_email})` : ""}
                {access?.reason ? `: ${access.reason}` : ""}. Expires on its own
                {remaining ? ` in ${remaining}` : ""}.
              </span>
              <button onClick={revokeFull} className="rounded bg-slate-800 px-3 py-1 text-white">
                Revoke
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-600">
                Normal mode: logs and safe commands. No shell.
              </span>
              <button onClick={grantFull} className="rounded bg-amber-600 px-3 py-1 text-white">
                Request full access
              </button>
            </div>
          )}
        </div>
      )}

      {hello && (
        <div className="grid gap-2 rounded border border-slate-200 bg-white p-3 text-sm sm:grid-cols-4">
          <div><span className="text-slate-500">Agent</span><br />{hello.agent_version}</div>
          <div><span className="text-slate-500">Python</span><br />{hello.python}</div>
          <div>
            <span className="text-slate-500">Free disk</span><br />
            {hello.disk_free_bytes != null ? `${(hello.disk_free_bytes / 1e9).toFixed(1)} GB` : "—"}
          </div>
          <div>
            <span className="text-slate-500">Shell on the device</span><br />
            {hello.allow_full_on_device ? "allowed" : "disabled"}
          </div>
        </div>
      )}

      <section className="rounded border border-slate-200 bg-white p-3">
        <h2 className="mb-2 text-sm font-semibold">Commands</h2>
        {commands.length === 0 ? (
          <p className="text-sm text-slate-500">
            The command list arrives from the device with its hello.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {commands.map((c) => (
              <button
                key={c}
                disabled={!!busy}
                onClick={() => runCommand(c)}
                className="rounded border border-slate-300 px-2 py-1 text-xs font-mono hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === c ? "…" : c}
              </button>
            ))}
          </div>
        )}
        {output && (
          <pre className="mt-3 max-h-72 overflow-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
            {JSON.stringify(output, null, 2)}
          </pre>
        )}
      </section>

      <section className="rounded border border-slate-200 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold">Terminal</h2>
          {ptySession ? (
            <button
              onClick={() => setPtySession(null)}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            >
              Close session
            </button>
          ) : (
            <button
              disabled={!hasPty}
              onClick={() => setPtySession(`${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`)}
              className="rounded bg-slate-800 px-3 py-1 text-xs text-white disabled:bg-slate-300"
            >
              Open shell
            </button>
          )}
          {!hasPty && (
            <span className="text-xs text-slate-500">
              {mode === "full"
                ? "the device did not grant shell access"
                : "full access needed - request it above"}
            </span>
          )}
        </div>
        {ptySession ? (
          <DeviceTerminal
            channel={channelRef.current}
            group={channelRef.current?.ptyGroup}
            sessionId={ptySession}
            onClosed={() => setPtySession(null)}
          />
        ) : (
          <p className="text-xs text-slate-500">
            An interactive session on the device. It opens only while full access is
            granted, is recorded, and closes on its own when the grant expires.
          </p>
        )}
      </section>

      <section className="rounded border border-slate-200 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Logs</h2>
          <button onClick={toggleStream} className="rounded border border-slate-300 px-2 py-1 text-xs">
            {streaming ? "Stop" : "Stream"}
          </button>
          <button onClick={() => setPaused((p) => !p)} className="rounded border border-slate-300 px-2 py-1 text-xs">
            {paused ? "Resume" : "Pause"}
          </button>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter"
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <span className="text-xs text-slate-500">
            {visibleLines.length} lines{dropped > 0 ? ` · dropped ${dropped}` : ""}
          </span>
        </div>
        {dropped > 0 && (
          <p className="mb-2 text-xs text-amber-700">
            The device dropped {dropped} lines to stay within its rate limit - the output is incomplete.
          </p>
        )}
        <pre className="max-h-96 overflow-auto rounded bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">
          {visibleLines.length ? visibleLines.join("\n") : "- no data -"}
          <div ref={logEndRef} />
        </pre>
      </section>
    </div>
  );
}
